import { useState } from 'react';
import { Autocomplete, TextField, Chip, Grid } from '@mui/material';

type Option = {
    label: string;
    value: string | number;
};

type Props = {
    grid?: boolean;
    name: string;
    caption: string;
    options: Option[];
    defVal: string;
    onChange: (event: { name: string | undefined; value: unknown }) => void;
    renderTags?: (
        value: Option[],
        getTagProps: (params: { index: number }) => Record<string, unknown>,
    ) => React.ReactNode;
    [key: string]: unknown;
};

/*
 * SttSelectMultiple renders a Select with Autocomplete and multi-selection
 * string name: name of the field
 * string caption: label for the text field
 * array options: [{label, value}]
 * string defVal: comma separated values like "Val1,Val2,Val3"... basically the currently selected options
 * func onChange: what to do on option added / removed
 * func renderTags: optional function with signature: (value, getTagProps) => value.map((option, index)) => <react nodes> to specify rendering of the selected options (tags)
 * */
export function SttSelectMultiple({
    grid,
    name,
    caption,
    options,
    defVal,
    onChange,
    renderTags,
    ...rest
}: Props): React.ReactElement {
    const values = defVal ? defVal.split(',') : [];
    const [value, setValue] = useState<Option[]>(
        options.filter((option: any) =>
            values.includes(option.value.toString()),
        ),
    );

    if (!renderTags) {
        renderTags = (value: Option[], getTagProps) =>
            value.map((option: any, index: any) => (
                <Chip
                    key={index}
                    size="small"
                    label={option.label}
                    {...getTagProps({ index })}
                />
            ));
    }

    const content = (
        <Autocomplete
            multiple
            options={options}
            getOptionLabel={(option: any) => option.label}
            value={value}
            onChange={(_ev: any, options: any) => {
                const reducedVal = options.reduce(
                    (res: any, opt: any, i: any) =>
                        res + ((i > 0 ? ',' : '') + opt.value),
                    '',
                );
                setValue(options);
                onChange({ name, value: reducedVal });
            }}
            filterOptions={(options: any, _state: any) => {
                if (!value || value.length === 0) {
                    return options;
                }
                return options.filter(
                    (option: any) =>
                        !value.find((opt: any) => opt.value === option.value),
                );
            }}
            filterSelectedOptions
            renderInput={(params: any) => (
                <TextField
                    {...params}
                    fullWidth
                    size="small"
                    label={caption}
                    variant="outlined"
                />
            )}
            renderTags={renderTags}
        />
    );

    return grid ? <Grid {...rest}>{content}</Grid> : content;
}
